import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../services/dashboardService';
import { toast } from 'react-hot-toast';

/**
 * Custom hook for managing dashboard data
 * @param {string|Object} optionsOrDataView - Either dataView string or options object with dateRange
 * @returns {Object} Dashboard data and loading state
 */
export const useDashboardData = (optionsOrDataView = {}) => {
  // Support both old format (options object) and new format (dataView string)
  const dataView = typeof optionsOrDataView === 'string' ? optionsOrDataView : null;
  const options = typeof optionsOrDataView === 'object' ? optionsOrDataView : {};
  const { dateRange } = options;
  const [data, setData] = useState({
    totalBalance: { amount: 0, percentageChange: 0 },
    monthlyIncome: { amount: 0, percentageChange: 0 },
    monthlyExpenses: { amount: 0, percentageChange: 0 },
    spendingTrends: [],
    categoryBreakdown: [],
    transactionTrends: [],
    incomeTrends: [],
    savingsTrends: [],
    debtTrends: [],
    recentActivity: [],
    lastUpdated: null,
    isDemo: false,
    incomeCount: 0,
    accountCount: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async (signal, customDateRange = null) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🔄 Hook: Fetching dashboard data...', { dateRange: customDateRange || dateRange, dataView });
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('🔒 Hook: No auth token found, redirecting to login');
        navigate('/login');
        return;
      }

      console.log('🔍 Hook: Auth token found, proceeding with API call');
      
      try {
        const response = await dashboardService.getDashboardData(signal, customDateRange || dateRange, dataView);
        
        // Only update state if the request wasn't aborted
        if (signal && !signal.aborted) {
          console.log('✅ Hook: Dashboard data received from service:', response);
          console.log('✅ Hook: totalBalance from service:', response.totalBalance);
          console.log('✅ Hook: monthlyIncome from service:', response.monthlyIncome);
          console.log('✅ Hook: monthlyExpenses from service:', response.monthlyExpenses);
          
          // The response is already normalized by the service
          const dashboardData = {
            ...response,
            totalBalance: response.totalBalance || { amount: 0, percentageChange: 0 },
            monthlyIncome: response.monthlyIncome || { amount: 0, percentageChange: 0 },
            monthlyExpenses: response.monthlyExpenses || { amount: 0, percentageChange: 0 },
            lastUpdated: new Date().toISOString(),
            isDemo: response.isDemo || false,  // Store isDemo flag
            // Preserve incomeCount and accountCount from service response
            incomeCount: response.incomeCount ?? 0,
            accountCount: response.accountCount ?? 0
          };

          console.log('📊 Hook: Setting dashboard data state:', dashboardData);
          setData(dashboardData);
          setIsLoading(false);
        }
      } catch (apiError) {
        // Check if this is an abort error
        if (apiError.name === 'CanceledError' || apiError.name === 'AbortError' || 
            (apiError.message && apiError.message.includes('canceled'))) {
          console.log('🛑 Hook: API request was aborted, ignoring error');
          return; // Exit early without setting error state
        }
        
        console.error('🚨 Hook: API call failed with error:', apiError);
        console.error('🚨 Hook: API error details:', {
          message: apiError.message,
          status: apiError.response?.status,
          responseData: apiError.response?.data
        });
        
        throw apiError; // Re-throw for the outer catch block
      }
    } catch (err) {
      // Handle abort errors properly
      if (err.name === 'CanceledError' || err.name === 'AbortError' || 
          (err.message && err.message.includes('canceled'))) {
        console.log('🛑 Hook: Request was aborted, ignoring error');
        return;
      }
      
      // Only handle errors if the request wasn't aborted
      if (!signal || !signal.aborted) {
        console.error('❌ Hook: Error fetching dashboard data:', err);
        if (err.response?.status === 401) {
          console.log('🔒 Hook: Authentication error, redirecting to login');
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch dashboard data';
        setError(errorMessage);
        toast.error(`Dashboard data error: ${errorMessage}`);

        // Show empty state instead of fallback to demo data
        console.log('🚨 Hook: Using empty data state for non-demo users');
        setData({
          totalBalance: { amount: 0, percentageChange: 0 },
          monthlyIncome: { amount: 0, percentageChange: 0 },
          monthlyExpenses: { amount: 0, percentageChange: 0 },
          spendingTrends: [],
          categoryBreakdown: [],
          transactionTrends: [],
          incomeTrends: [],
          savingsTrends: [],
          debtTrends: [],
          recentActivity: [],
          lastUpdated: null,
          isDemo: false
        });
        setIsLoading(false);
      }
    } finally {
      // Only update loading state if the request wasn't aborted
      if (!signal || !signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [navigate, dataView, dateRange]);

  // Function to force using demo data
  const useDemo = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🎭 Hook: Requesting demo data...');

      // Check if current user is demo account
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const userEmail = parsedUser?.email;
        
        // Only allow demo data for the demo user account
        if (userEmail !== 'demo@thewallet.com') {
          console.log('🔒 Hook: Demo data restricted - current user is not demo account');
          toast.error('Demo data is only available for the demo account');
          setIsLoading(false);
          return;
        }
      }

      const demoData = await dashboardService.getDemoData();
      
      // Update with demo data
      const formattedDemoData = {
        ...demoData,
        totalBalance: demoData.totalBalance || { amount: 0, percentageChange: 0 },
        monthlyIncome: demoData.monthlyIncome || { amount: 0, percentageChange: 0 },
        monthlyExpenses: demoData.monthlyExpenses || { amount: 0, percentageChange: 0 },
        lastUpdated: new Date().toISOString(),
        isDemo: true
      };
      
      console.log('🎭 Hook: Setting demo data:', formattedDemoData);
      setData(formattedDemoData);
      toast.success('Using demo data for dashboard');
    } catch (error) {
      console.error('❌ Hook: Error loading demo data:', error);
      toast.error('Failed to load demo data');
      setError('Failed to load demo data');
      
      // Reset to empty state on error
      setData({
        totalBalance: { amount: 0, percentageChange: 0 },
        monthlyIncome: { amount: 0, percentageChange: 0 },
        monthlyExpenses: { amount: 0, percentageChange: 0 },
        spendingTrends: [],
        categoryBreakdown: [],
        transactionTrends: [],
        incomeTrends: [],
        savingsTrends: [],
        debtTrends: [],
        recentActivity: [],
        lastUpdated: null,
        isDemo: false
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔄 Hook: Dashboard hook initialized, fetching data...');
    const abortController = new AbortController();
    
    // Only trigger fetch after a delay to prevent race conditions
    const fetchTimer = setTimeout(() => {
      fetchDashboardData(abortController.signal);
    }, 100);

    return () => {
      // Cleanup: abort any in-flight requests and clear timer
      console.log('🧹 Hook: Cleaning up dashboard hook, aborting requests');
      clearTimeout(fetchTimer);
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, dataView]); // Refetch when dateRange OR dataView changes

  const refresh = useCallback(async (customDateRange = null) => {
    console.log('🔄 Hook: Manual refresh requested', { dateRange: customDateRange });
    const abortController = new AbortController();
    try {
      await fetchDashboardData(abortController.signal, customDateRange);
    } catch (error) {
      // Ignore AbortError
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        throw error;
      }
    }
    return () => abortController.abort();
  }, [fetchDashboardData]);

  const result = {
    ...data,
    isLoading,
    error,
    refresh,
    useDemo, // Add the useDemo function to the returned object
    lastUpdated: data.lastUpdated
  };
  
  console.log('🔄 Hook: Returning dashboard data to components:', {
    totalBalance: result.totalBalance,
    monthlyIncome: result.monthlyIncome,
    monthlyExpenses: result.monthlyExpenses,
    isLoading: result.isLoading,
    lastUpdated: result.lastUpdated
  });
  
  return result;
};

// Default export for backward compatibility
export default useDashboardData; 